import { clsx } from "clsx"
import {
  type AnimationEvent,
  type CSSProperties,
  forwardRef,
  type Key,
  type ReactElement,
  useState,
} from "react"

import { type SX, theme, type ThemeKeys } from "../../theme"
import styles from "./AnimatedElement.module.css"

export interface AnimatedElementProps {
  children: OnlyChild
  animation?:
    | { name: "fade" }
    | { name: "slide"; offset?: number }
    | { name: "expand-vertical" }
    | { name: "expand-horizontal" }
  enterDuration?: AnimationDuration
  exitDuration?: AnimationDuration
  focusable?: boolean
  sx?: SX
}

/**
 * Animates a single element in & out based on whether it's currently rendered.
 * An exit then enter animation will also play sequentially if the element's key changes.
 * Unlike React CSSTransition(Group), this is based on CSS animations rather than a bunch of transition classes
 * and the element doesn't have to be evaluated/rendered at all if it's not currently shown.
 *
 * If you only need to animate a single element's entrance (and not exit/swap), you don't need this component:
 * you can just set a CSS animation on the element styles.
 */
export const AnimatedElement = forwardRef<HTMLDivElement, AnimatedElementProps>(
  function AnimatedElement(props, ref) {
    const {
      children,
      animation = { name: "fade" },
      enterDuration = 300,
      exitDuration = enterDuration,
      focusable,
      sx,
    } = props

    const [state, setState] = useState(defaultStateFromChild(children))

    const newState = computeNewState(state, props)
    if (newState !== state) setState(newState)

    const childToDisplay = state.type === "absent" ? null : state.child

    const className = clsx(
      styles.animatedElement,
      state.type === "entering" && styles[`entering-${animation.name}`],
      state.type === "exiting" && styles[`exiting-${animation.name}`],
    )

    const animationOffset =
      animation.name === "slide" ? (animation.offset ?? 40) : 0

    const style: CSSProperties = {
      ...sx,
      "--AnimatedElement-offset": `${animationOffset}px`,
      animationDuration: getAnimationDuration(
        state,
        enterDuration,
        exitDuration,
      ),
    }

    function onAnimationEnd(evt: AnimationEvent) {
      // Bubbling event: Ignore.
      if (evt.target !== evt.currentTarget) return

      if (state.type === "entering") {
        setState({ type: "present", child: state.child })
      } else if (state.type === "exiting") {
        setState({ type: "absent" })
      }
    }

    return (
      <div
        ref={ref}
        className={className}
        tabIndex={focusable ? -1 : undefined}
        style={style}
        onAnimationEnd={onAnimationEnd}
      >
        {childToDisplay}
      </div>
    )
  },
)

type AnimationDuration = ThemeKeys<"transitionDuration">

type OnlyChild = ReactElement | null | undefined | false | 0

type State =
  | { type: "exiting"; child: ReactElement }
  | { type: "entering"; child: ReactElement }
  | { type: "present"; child: ReactElement }
  | { type: "absent" }

function getChildKey(child: ReactElement): Key {
  return child.key ?? "__KEY__"
}

function defaultStateFromChild(child: OnlyChild): State {
  return child ? { type: "present", child } : { type: "absent" }
}

function getAnimationDuration(
  state: State,
  enterDuration: AnimationDuration,
  exitDuration: AnimationDuration,
) {
  if (state.type === "entering") return theme.transitionDuration[enterDuration]
  if (state.type === "exiting") return theme.transitionDuration[exitDuration]
  return undefined
}

function computeNewState(
  state: Readonly<State>,
  props: AnimatedElementProps,
): State {
  const { children, exitDuration } = props

  // Transitioning to no child.
  if (!children) {
    // From an entering or present child: Time to exit it.
    if (state.type === "entering" || state.type === "present")
      return getExitingState(state.child, exitDuration)

    // From an absent or exiting child: Nothing to do.
    return state
  }

  // Transitioning from no child to a child: The new child enters.
  if (state.type === "absent") return { type: "entering", child: children }

  // Transitions from a defined child to another defined child
  const oldChildKey = getChildKey(state.child)
  const newChildKey = getChildKey(children)

  // We still have the same child: No transition.
  if (oldChildKey === newChildKey) {
    // Update the state's child to reflect the latest children reference, which could have updated props.
    if (state.type === "present" && state.child !== children)
      return { type: "present", child: children }

    // If we're in the middle of an animation, or if we can keep a stable reference, just keep using the existing child:
    // No need to repaint the latest changes mid-animation.
    return state
  }

  // Different child but already exiting: nothing to do.
  if (state.type === "exiting") return state

  // Different child and old child is still entering or present: Exit it.
  return getExitingState(state.child, exitDuration)
}

function getExitingState(child: ReactElement, exitDuration?: number): State {
  return exitDuration === 0 ? { type: "absent" } : { type: "exiting", child }
}
